import { SearchFilter } from "../../../common/components/functional/FilterSearchField/FilterSearchField";
import {
  dateToFormatOrEmpty,
  makeLookupValues,
  makeMultiSelectDisplay,
  parseAutocomplete,
  replaceCommasFromValue,
  toStringArray,
} from "../../../common/helpers/filters";
import { isoFormat } from "../../../common/helpers/formatDateTime";
import { AutocompleteType } from "../../../common/types/autoComplete";
import { AutocompleteCustomerCC } from "../../customer/customerAPI";

export type InvoiceFilterFormTypes = {
  invoiceTypes: string;
  invoiceDateFrom: Date | null;
  invoiceDateTo: Date | null;
  invoiceDueDateFrom: Date | null;
  invoiceDueDateTo: Date | null;
  invoiceValueDateFrom: Date | null;
  invoiceValueDateTo: Date | null;
  invoiceStatuses: string[];
  customerOrganizationNames: string;
  // company names, not ids
  eezyCompanyNames: string[];
  salesPersons: AutocompleteType[];
  invoiceWaves: string;
  vatExcludedAmountFrom: string;
  vatExcludedAmountTo: string;
  vatAmountFrom: string;
  vatAmountTo: string;
  customerCC: AutocompleteCustomerCC | null;
  // ids: BillingType enum values, e.g. SalaryHourly
  billingTypes: AutocompleteType[];
};

export const DEFAULT_INVOICE_FORM: InvoiceFilterFormTypes = {
  invoiceTypes: "",
  invoiceDateFrom: null,
  invoiceDateTo: null,
  invoiceDueDateFrom: null,
  invoiceDueDateTo: null,
  invoiceValueDateFrom: null,
  invoiceValueDateTo: null,
  invoiceStatuses: [],
  customerOrganizationNames: "",
  eezyCompanyNames: [],
  salesPersons: [],
  invoiceWaves: "",
  vatExcludedAmountFrom: "",
  vatExcludedAmountTo: "",
  vatAmountFrom: "",
  vatAmountTo: "",
  customerCC: null,
  billingTypes: [],
};

// Form values -> filters (chips + query params)
export const buildInvoiceFilters = (
  values: InvoiceFilterFormTypes,
): SearchFilter[] => {
  const {
    invoiceTypes,
    invoiceDateFrom,
    invoiceDateTo,
    invoiceDueDateFrom,
    invoiceDueDateTo,
    invoiceValueDateFrom,
    invoiceValueDateTo,
    invoiceStatuses,
    customerOrganizationNames,
    eezyCompanyNames,
    salesPersons,
    invoiceWaves,
    vatExcludedAmountFrom,
    vatExcludedAmountTo,
    vatAmountFrom,
    vatAmountTo,
    customerCC,
    billingTypes,
  } = values;

  const { displayValue: eezyCompaniesDisplay, tooltip: eezyCompaniesTooltip } =
    makeMultiSelectDisplay(eezyCompanyNames.map((name) => ({ name })));
  const { displayValue: salesPersonDisplay, tooltip: salesPersonTooltip } =
    makeMultiSelectDisplay(salesPersons);
  const { displayValue: billingTypesDisplay, tooltip: billingTypesTooltip } =
    makeMultiSelectDisplay(billingTypes);

  return [
    { name: "invoiceTypes", value: invoiceTypes },
    {
      name: "invoiceDateFrom",
      value: dateToFormatOrEmpty(invoiceDateFrom, isoFormat),
      type: "date",
    },
    {
      name: "invoiceDateTo",
      value: dateToFormatOrEmpty(invoiceDateTo, isoFormat),
      type: "date",
    },
    {
      name: "invoiceDueDateFrom",
      value: dateToFormatOrEmpty(invoiceDueDateFrom, isoFormat),
      type: "date",
    },
    {
      name: "invoiceDueDateTo",
      value: dateToFormatOrEmpty(invoiceDueDateTo, isoFormat),
      type: "date",
    },
    {
      name: "invoiceValueDateFrom",
      value: dateToFormatOrEmpty(invoiceValueDateFrom, isoFormat),
      type: "date",
    },
    {
      name: "invoiceValueDateTo",
      value: dateToFormatOrEmpty(invoiceValueDateTo, isoFormat),
      type: "date",
    },
    { name: "invoiceStatuses", value: invoiceStatuses },
    {
      name: "customerOrganizationNames",
      value: replaceCommasFromValue(customerOrganizationNames),
      displayValue: customerOrganizationNames,
    },
    {
      name: "eezyCompanyNames",
      value: eezyCompanyNames,
      displayValue: eezyCompaniesDisplay,
      ...(eezyCompaniesTooltip && { tooltip: eezyCompaniesTooltip }),
    },
    {
      name: "salesPersonIds",
      value: salesPersons.map((sp) => sp.id),
      displayValue: salesPersonDisplay,
      ...(salesPersonTooltip && { tooltip: salesPersonTooltip }),
      lookupValues: makeLookupValues(salesPersons),
    },
    { name: "invoiceWaves", value: invoiceWaves },
    { name: "vatExcludedAmountFrom", value: vatExcludedAmountFrom || "" },
    { name: "vatExcludedAmountTo", value: vatExcludedAmountTo || "" },
    { name: "vatAmountFrom", value: vatAmountFrom || "" },
    { name: "vatAmountTo", value: vatAmountTo || "" },
    {
      name: "customerCC",
      value: customerCC?.id || "",
      displayValue: customerCC?.name || "",
      displayName: "customerCostCenter",
      // In this case, use lookup values to save customer org info
      lookupValues: [
        {
          id: customerCC?.customerOrgId || "",
          name: customerCC?.customerOrgName || "",
        },
      ],
    },
    {
      name: "billingTypes",
      value: billingTypes.map((billingType) => billingType.id),
      displayName: "billingType",
      displayValue: billingTypesDisplay,
      ...(billingTypesTooltip && { tooltip: billingTypesTooltip }),
      lookupValues: makeLookupValues(billingTypes),
    },
  ];
};

const parseDate = (filter: SearchFilter | undefined): Date | null =>
  filter?.value ? new Date(filter.value) : null;

// Filters -> form values. Tolerates missing filters (e.g. stale localStorage).
export const parseInvoiceFilters = (
  filters: SearchFilter[],
): InvoiceFilterFormTypes => {
  const find = (name: string) => filters.find((f) => f.name === name);

  const customerOrgFilter = find("customerOrganizationNames");
  const customerCCFilter = find("customerCC");

  return {
    invoiceTypes: find("invoiceTypes")?.value || "",
    invoiceDateFrom: parseDate(find("invoiceDateFrom")),
    invoiceDateTo: parseDate(find("invoiceDateTo")),
    invoiceDueDateFrom: parseDate(find("invoiceDueDateFrom")),
    invoiceDueDateTo: parseDate(find("invoiceDueDateTo")),
    invoiceValueDateFrom: parseDate(find("invoiceValueDateFrom")),
    invoiceValueDateTo: parseDate(find("invoiceValueDateTo")),
    invoiceStatuses: toStringArray(find("invoiceStatuses")?.value),
    // value has commas replaced, so the name is read from the display value
    customerOrganizationNames: customerOrgFilter?.value
      ? customerOrgFilter.displayValue || ""
      : "",
    eezyCompanyNames: toStringArray(find("eezyCompanyNames")?.value),
    salesPersons: parseAutocomplete(find("salesPersonIds")),
    invoiceWaves: find("invoiceWaves")?.value || "",
    vatExcludedAmountFrom: find("vatExcludedAmountFrom")?.value || "",
    vatExcludedAmountTo: find("vatExcludedAmountTo")?.value || "",
    vatAmountFrom: find("vatAmountFrom")?.value || "",
    vatAmountTo: find("vatAmountTo")?.value || "",
    customerCC:
      customerCCFilter?.value && customerCCFilter.displayValue
        ? {
            id: customerCCFilter.value,
            name: customerCCFilter.displayValue,
            customerOrgId: customerCCFilter.lookupValues?.[0]?.id || "",
            customerOrgName: customerCCFilter.lookupValues?.[0]?.name || "",
          }
        : null,
    billingTypes: parseAutocomplete(find("billingTypes")),
  };
};
